---
title: "94% stron polskich urzędów narusza WCAG — pięć najczęstszych błędów"
slug: 94-procent-stron-urzedow-narusza-wcag
date: 2026-03-10
description: "948 tysięcy z miliona stron ma błędy dostępności — dane WebAIM 2025. W Polsce żadna z 100 zbadanych stron publicznych nie była zgodna z ustawą 2019. Oto pięć wzorców które widzimy wszędzie."
tags: [WCAG 2.2, dostępność cyfrowa, ustawa 2019, audyt, instytucje publiczne]
category: "Dane"
readingTime: 5
---

Na milion przebadanych stron — 948 tysięcy miało błędy dostępności. To dane [WebAIM z 2025 roku](https://webaim.org/projects/million/), nie nasze szacunki.

100 stron polskich instytucji publicznych. Żadna nie była w pełni zgodna z ustawą z 4 kwietnia 2019 r. Nie częściowo — żadna. Dane Ministerstwa Cyfryzacji, 2024 rok.

Ponad 35 własnych audytów — sektor publiczny i prywatny — pokazuje te same pięć wzorców.

---

## #1 — Obrazy bez opisu słownego

72 razy. Tyle razy niewidomy użytkownik słyszał „link, bez nazwy" na jednej stronie w ciągu jednego przejścia.

72 linki z logo urzędu — każde bez atrybutu `alt`. Czytnik ekranu nie wiedział dokąd prowadzi. Użytkownik też nie.

Problem dotyczy ponad 9 z 10 stron w naszych audytach. Globalnie — 555 tysięcy z miliona przebadanych stron ma ten błąd ([WebAIM 2025](https://webaim.org/projects/million/)). Polskie instytucje wypadają gorzej niż ta średnia.

Przepis jest jednoznaczny: WCAG 1.1.1, poziom A, Art. 5 ust. 1 Ustawy z 4.04.2019 r. Zasada prosta — każdy obraz ma opis. Zdjęcie budynku? Co na nim jest. Logo w linku? Dokąd prowadzi. Ikona dekoracyjna? Oznacz ją jako dekoracyjną — czytnik ją pominie.

---

## #2 — Przyciski i linki bez nazwy

77 linków. Każdy: „link, bez nazwy".

Kalendarz wydarzeń na jednej ze zbadanych stron samorządowych. 77 pozycji — i żadna bez dostępnej etykiety. Menu hamburger bez opisu, strzałki karuzeli bez nazwy, ikony mediów społecznościowych bez tekstu alternatywnego. Czytnik ekranu ogłasza: „przycisk". Bez nazwy. Nikt niewidomy nie był w stanie korzystać z tego kalendarza.

Niemal 9 z 10 zbadanych stron ma ten problem. Globalnie: puste linki na 45,4% stron, puste przyciski na 29,6% ([WebAIM 2025](https://webaim.org/projects/million/)).

Każdy przycisk i link musi mieć nazwę zrozumiałą bez patrzenia na ikonę — to wymóg WCAG 4.1.2 (poziom A) i Art. 5 ust. 1 Ustawy. Ikona menu? „Otwórz menu". Strzałka karuzeli? „Następne wydarzenie". Logo Facebooka? „Facebook — otwiera nową kartę".

---

## #3 — Formularze bez etykiet

Kliknij w pole e-mail na swojej stronie. Gdzie jest opis?

Jeśli zniknął — masz problem. Placeholder to wskazówka, nie etykieta. Użytkownik czytnika ekranu nie wiedział co wpisać, zanim jeszcze kliknął. Użytkownik z zaburzeniem pamięci krótkotrwałej nie wie tego po kliknięciu.

Formularz kontaktowy, wniosek, zapis na wydarzenie — każdy bez etykiety blokuje dostęp do usługi publicznej. To naruszenie WCAG 1.3.1 (poziom A) i Art. 5 ust. 1 Ustawy z 4.04.2019 r. Dotyczy ponad 8 z 10 stron w naszych audytach; globalnie — 48,2% stron ([WebAIM 2025](https://webaim.org/projects/million/)).

Naprawa jest prosta: widoczna etykieta nad polem, zawsze. Nawet gdy pole jest puste.

{/* TODO: link-internal — link do posta o formularzach i WCAG 1.3.1 gdy będzie dostępny */}

---

## #4 — Brak linku „Przejdź do treści"

Tab. Raz. Jeśli kursor nie przeskoczył do linku „Przejdź do treści" — skip link nie istnieje.

Na jednej z audytowanych stron menu miało 23 pozycje. Każda wizyta na podstronie: 23 naciśnięcia Tab zanim zaczniesz czytać. Ponad 6 z 10 zbadanych stron nie ma tego elementu — choć globalnie tylko 13,7% stron go posiada ([WebAIM 2025](https://webaim.org/projects/million/)). Brak jest normą. To nie jest powód żeby go nie dodać.

Jeden element na początku kodu strony. Pozwala przeskoczyć całą nawigację i trafić od razu do treści. To wymóg WCAG 2.4.1 (poziom A) i Art. 5 ust. 1 Ustawy.

---

## #5 — Niewystarczający kontrast kolorów

Kontrast 2,97:1. Wymagane 4,5:1. To jak czytanie szarego tekstu przez zaparowaną szybę.

Szary tekst na białym tle, jasnoniebieski link na jasnym tle, pomarańczowy przycisk z białym tekstem — na świecie kontrast to błąd numer 1, pojawia się na 4 z 5 stron (79,1% — [WebAIM 2025](https://webaim.org/projects/million/)). W polskich urzędach mamy ten sam odsetek.

Kogo to dotyczy? Osób starszych, osób z niedowidzeniem, osób z daltonizmem. W polskich urzędach to duża część petentów.

Podstawa prawna: WCAG 1.4.3 (poziom AA) i Art. 5 ust. 1 Ustawy. Darmowy WebAIM Contrast Checker (webaim.org/resources/contrastchecker) — wpisz dwa kolory, wynik poniżej 4,5:1 to błąd WCAG.

---

## Jakie konsekwencje grożą za naruszenie ustawy 2019?

Ustawa z 4.04.2019 r. nie przewiduje bezpośrednich kar finansowych dla podmiotów publicznych — ale mechanizm egzekucji istnieje i jest coraz szerzej stosowany:

- Skarga do PFRON — każdy użytkownik może ją złożyć. PFRON wszczyna postępowanie administracyjne zakończone nakazem naprawczym
- Nakaz dostosowania strony (Art. 18) z terminem — niewykonanie nakazu uruchamia egzekucję administracyjną
- Negatywny wpis w raporcie MC — coroczny raport z monitoringu jest publiczny i trafia do Komisji Europejskiej

{/* TODO: link-internal — link do posta o procedurze PFRON i mechanizmie skargi */}

Szczegółowy opis procedury wniosku i terminów: [Wniosek z Art. 30 dotarł do urzędu. Masz 14 dni.](/blog/wniosek-art-30-masz-14-dni)

Deklaracja dostępności musi być aktualizowana **co roku, do 31 marca.**

---

## Plan minimum: WCAG poziom A w pięciu krokach

Trzy pierwsze błędy z listy — brak opisów obrazów, brak nazw elementów interaktywnych, brak etykiet formularzy — to naruszenia poziomu A. Powodują największe wykluczenie i niosą najwyższe ryzyko prawne.

1. Opisz wszystkie obrazy — zdjęcia, ikony, logotypy, banery informacyjne
2. Nazwij wszystkie przyciski i linki — czy bez patrzenia na ikonę wiadomo, co to jest?
3. Dodaj etykiety do formularzy — widoczne opisy pól, nie znikające podpowiedzi
4. Dodaj skip link — jeden element na początku strony
5. Sprawdź deklarację dostępności — czy istnieje, jest aktualna i zawiera dane kontaktowe

---

Pięć błędów, jedna lista. Pierwszą rzecz z niej możesz sprawdzić przed zamknięciem przeglądarki.

Tab. Jedna sekunda. Jeśli kursor nie przeskoczył do treści — wiesz co zrobić.
